import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Container } from "../layout/Container.jsx";
import { fetchFeaturedFacades } from "../../api/public.api.js";
import { assetUrl } from "../../api/client.js";
import { placeholderImage, imgErrorFallback } from "../../lib/placeholders.js";
import { ImageWithSkeleton } from "../shared/ImageWithSkeleton.jsx";

const ABOUT_TITLE = "Designs you customize, builders who deliver";
const ABOUT_BODY =
  "Every Shere-e-Punjab home is drawn in-house, built by our own teams, and backed by a fixed-price guarantee. Whether it's a 3-bedroom Plato or our flagship Royale 42, you talk to the same project lead from sketch to keys.";

/**
 * About strip — credibility-builder section. Image collage now uses real
 * facade photography from the backend instead of generic stock so the
 * imagery matches the rest of the brand. Falls back to the curated
 * placeholder pool only when the API has nothing to return.
 */
export function AboutCustomize() {
  const title = ABOUT_TITLE;
  const body = ABOUT_BODY;

  const { data: facades } = useQuery({
    queryKey: ["public", "facades", "featured", 6],
    queryFn: () => fetchFeaturedFacades(6),
    staleTime: 5 * 60_000,
  });

  // Pick two distinct shots — one tall feature, one square thumbnail.
  const facade1 = facades?.[0]?.image;
  const facade2 = facades?.[1]?.image ?? facades?.[0]?.image;
  const img1 = facade1 ? assetUrl(facade1) : placeholderImage("design", "about-customize-1");
  const img2 = facade2 ? assetUrl(facade2) : placeholderImage("facade", "about-customize-2");

  return (
    <section className="border-t border-border-subtle bg-bg-elevated py-20 lg:py-28">
      <Container>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 lg:items-center">
          {/* Image collage — real facade photography */}
          <div className="relative grid aspect-[5/4] grid-cols-2 grid-rows-2 gap-3">
            <Link
              to="/facades"
              className="group relative row-span-2 overflow-hidden rounded-2xl bg-bg-elevated-2"
              aria-label="Browse our facade collection"
            >
              <ImageWithSkeleton
                src={img1}
                onError={imgErrorFallback("about-1")}
                alt=""
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </Link>
            <Link
              to="/facades"
              className="group relative overflow-hidden rounded-2xl bg-bg-elevated-2"
              aria-label="Browse our facade collection"
            >
              <ImageWithSkeleton
                src={img2}
                onError={imgErrorFallback("about-2")}
                alt=""
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </Link>
            <div className="grid place-items-center overflow-hidden rounded-2xl border border-accent/30 bg-accent/5 p-6 text-center">
              <div>
                <p className="font-display text-4xl text-accent">20+</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-text-muted">
                  Years building
                </p>
              </div>
            </div>
            {/* Decorative corner accent */}
            <span
              aria-hidden
              className="absolute -bottom-4 -right-4 h-24 w-24 rounded-full bg-accent/10 blur-2xl"
            />
          </div>

          {/* Copy */}
          <div>
            <p className="eyebrow">Custom-built</p>
            <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
              {title}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-text-secondary">{body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about/company-profile" className="btn-outline">
                About Shere-e-Punjab Homes
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/about/why-build-with-us" className="text-sm text-accent hover:text-accent-hover lg:py-2.5">
                Why build with us →
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
